import type { ReactNode } from 'react';
import { IconClock, IconFlag, IconMapPin } from '@tabler/icons-react';
import { RIDES_SEX, type RidesVehicleView } from '@actschurch/ccms-sdk';
import { formatDate, formatShortDate, formatTime, stampToDate } from '@/lib/format';
import { PersonAvatar } from '@/components/rides/PersonAvatar';
import { PlateChip } from '@/components/rides/PlateChip';
import { vehicleLabel } from '@/components/rides/VehicleLine';
import '@/styles/flows.css';

/** "Toyota Corolla Quest, silver" (colour in lower case after the comma). */
export function carWithColour(v: RidesVehicleView | null | undefined): string {
  if (!v) return '';
  const label = vehicleLabel(v);
  const colour = v.colour?.trim();
  return colour ? `${label}, ${colour.toLowerCase()}` : label;
}

/** "08:52" for a yyyyMMddHHmmss stamp; with `withDay`, "Sun 11 Oct at 08:52" unless it is today. */
export function stampClock(stamp: number | null | undefined, withDay = false): string {
  const d = stampToDate(stamp);
  if (!d) return '';
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (!withDay) return hm;
  const now = new Date();
  const today = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return today ? hm : `${formatShortDate(d)} at ${hm}`;
}

export interface LiftTimes {
  date: string | number;
  departureTime?: string | null;
  serviceTimeStart?: string | null;
}

/** "Leaves at 09:00" / "Time to be confirmed". */
export function leavesText({ departureTime }: LiftTimes): string {
  return departureTime ? `Leaves at ${formatTime(departureTime)}` : 'Time to be confirmed';
}

interface Props extends LiftTimes {
  /** Small heading above the card body ("Your lift"). */
  eyebrow?: ReactNode;
  driverName: string;
  driverSexId?: number | null;
  /** Already filtered by the photo settings (null = initials). */
  driverPhotoUrl?: string | null;
  vehicle: RidesVehicleView | null | undefined;
  collectionName: string;
  dropOffName: string;
  eventName?: string;
  /** The driver's note for riders. */
  notes?: string | null;
}

/**
 * The lift being booked or already booked: who is driving, which car (with the number plate),
 * when it leaves and the two campuses. Shown on the booking step so members can see exactly what
 * they are booking.
 */
export function LiftCard({ eyebrow, driverName, driverSexId, driverPhotoUrl, vehicle, collectionName, dropOffName, eventName, notes, ...times }: Props) {
  return (
    <section className="rides-card rides-lift" aria-label={typeof eyebrow === 'string' ? eyebrow : 'Your lift'}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <div className="head">
        <PersonAvatar src={driverPhotoUrl} name={driverName} size={64} color={driverSexId === RIDES_SEX.Female ? 'pink' : 'brand'} decorative />
        <div className="txt">
          <b>{driverName}</b>
          <span className="car">{carWithColour(vehicle)}</span>
          <PlateChip plate={vehicle?.plate} />
        </div>
      </div>
      <ul className="rides-iconrows">
        <li>
          <IconClock size={20} aria-hidden="true" />
          <span className="t">{leavesText(times)}<small>{formatDate(times.date)}</small></span>
        </li>
        <li>
          <IconMapPin size={20} aria-hidden="true" />
          <span className="t">From {collectionName}</span>
        </li>
        <li>
          <IconFlag size={20} aria-hidden="true" />
          <span className="t">
            To {dropOffName}
            {(times.serviceTimeStart || eventName) && (
              <small>{times.serviceTimeStart ? `For the ${formatTime(times.serviceTimeStart)} service` : eventName}</small>
            )}
          </span>
        </li>
      </ul>
      {notes?.trim() && <p className="rides-help" style={{ marginTop: 12 }}>Note from {driverName.split(' ')[0]}: “{notes.trim()}”</p>}
    </section>
  );
}
